/**
 * Speurtocht Play Screen
 * Main screen for playing a speurtocht - shows current step
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Alert,
  ActivityIndicator,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  Image,
  Linking,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import LottieView from "lottie-react-native";
import * as ImagePicker from "expo-image-picker";
import * as Location from "expo-location";
import { supabase } from "@/lib/supabase/client";
import {
  useSpeurtocht,
  useLocationCheck,
  formatDistance,
  SpeurttochtStep,
  SpeurttochtStepCompletion,
  STEP_TYPE_CONFIG,
  STEP_TYPE_COLORS,
  QRCodeScanner,
} from "@/modules/speurtocht";
import { getLottieSource } from "@/lib/lottie-registry";

/**
 * Timer component
 */
interface TimerProps {
  expiresAt: string | null;
  colors: ReturnType<typeof useThemeColors>;
}

const Timer = ({ expiresAt, colors }: TimerProps) => {
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [isUrgent, setIsUrgent] = useState(false);

  useEffect(() => {
    if (!expiresAt) return;

    const updateTimer = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeLeft("Tijd voorbij!");
        setIsUrgent(true);
        return;
      }

      const minutes = Math.floor(diff / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft(`${minutes}:${seconds.toString().padStart(2, "0")}`);
      setIsUrgent(minutes < 5);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (!expiresAt) return null;

  return (
    <View
      className="flex-row items-center px-3 py-2 rounded-lg"
      style={{
        backgroundColor: isUrgent ? `${colors.error}20` : colors.surface,
      }}
    >
      <Ionicons
        name="time"
        size={18}
        color={isUrgent ? colors.error : colors.textSecondary}
      />
      <Text
        className="ml-2 font-bold"
        style={{ color: isUrgent ? colors.error : colors.text }}
      >
        {timeLeft}
      </Text>
    </View>
  );
};

export default function SpeurttochtPlayScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const colors = useThemeColors();
  const { activeProfile, userTeam } = useAuth();

  const {
    speurtocht,
    session,
    completions,
    currentStep,
    completedSteps,
    isLoading,
    isSubmitting,
    error,
    refetch,
    startSpeurtocht,
    completeStep,
  } = useSpeurtocht({ speurttochtId: id || "" });

  // Get steps from speurtocht
  const steps = speurtocht?.steps || [];

  // Geocoded coordinates state
  const [geocodedCoords, setGeocodedCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);

  // Build target config for location step
  const locationConfig = React.useMemo(() => {
    if (!currentStep || currentStep.step_type !== "location") return null;
    const config = currentStep.config as Record<string, unknown>;
    
    // Try to get coordinates from config or from geocoded address
    const lat = Number(config.latitude || config.lat || geocodedCoords?.lat || 0);
    const lng = Number(config.longitude || config.lng || geocodedCoords?.lng || 0);
    
    return {
      lat,
      lng,
      radius_meters: Number(config.radius_meters || 50),
      show_map: Boolean(config.show_map ?? true),
      address: (config.address as string) || undefined,
    };
  }, [currentStep, geocodedCoords]);

  // Geocode address when location step is loaded
  useEffect(() => {
    const geocodeAddress = async () => {
      if (!currentStep || currentStep.step_type !== "location") return;
      
      const config = currentStep.config as Record<string, unknown>;
      const hasCoords = config.latitude || config.lat;
      const address = config.address as string;
      
      // Skip if we already have coordinates or no address
      if (hasCoords || !address) return;
      
      setIsGeocoding(true);
      setGeocodeError(null);
      
      try {
        // Request location permission first (required for geocoding on some devices)
        const { status } = await Location.requestForegroundPermissionsAsync();
        
        if (status !== "granted") {
          // Try geocoding anyway - it might work on some platforms without permission
          console.log("Location permission not granted, trying geocoding anyway...");
        }
        
        const results = await Location.geocodeAsync(address);
        
        if (results.length > 0) {
          setGeocodedCoords({
            lat: results[0].latitude,
            lng: results[0].longitude,
          });
        } else {
          setGeocodeError("Adres niet gevonden");
        }
      } catch (err: any) {
        console.error("Geocoding error:", err);
        // Check if it's a permission error
        if (err?.message?.includes("Not authorized") || err?.message?.includes("permission")) {
          setGeocodeError("Locatie toestemming vereist. Gebruik de 'Navigeer' knop.");
        } else {
          setGeocodeError("Kon adres niet vinden");
        }
      } finally {
        setIsGeocoding(false);
      }
    };
    
    geocodeAddress();
  }, [currentStep]);

  // Location check hook - only initialize when we have a location step
  const {
    distanceToTarget,
    isWithinRadius,
    isLoading: isLocationLoading,
    getCurrentLocation,
    currentLocation,
    error: locationError,
    hasPermission,
    requestPermission,
  } = useLocationCheck({
    targetConfig: locationConfig || { lat: 0, lng: 0, radius_meters: 50, show_map: false },
    autoStart: false,
  });

  /**
   * Open maps app for navigation
   */
  const handleOpenMaps = () => {
    if (!locationConfig) return;
    
    const { lat, lng, address } = locationConfig;
    
    // Use coordinates if available, otherwise use address
    let url: string;
    if (lat && lng && lat !== 0 && lng !== 0) {
      // iOS prefers Apple Maps, Android prefers Google Maps
      if (Platform.OS === "ios") {
        url = `maps://app?daddr=${lat},${lng}`;
      } else {
        url = `google.navigation:q=${lat},${lng}`;
      }
    } else if (address) {
      const encodedAddress = encodeURIComponent(address);
      if (Platform.OS === "ios") {
        url = `maps://app?daddr=${encodedAddress}`;
      } else {
        url = `google.navigation:q=${encodedAddress}`;
      }
    } else {
      Alert.alert("Geen locatie", "Er is geen locatie beschikbaar voor navigatie");
      return;
    }
    
    Linking.canOpenURL(url).then((supported) => {
      if (supported) {
        Linking.openURL(url);
      } else {
        // Fallback to Google Maps web URL
        const webUrl = lat && lng && lat !== 0 && lng !== 0
          ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
          : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address || "")}`;
        Linking.openURL(webUrl);
      }
    });
  };

  // Form state
  const [answer, setAnswer] = useState("");
  const [showHint, setShowHint] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const lottieRef = useRef<LottieView>(null);

  // QR scanner state
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [scannedQRValue, setScannedQRValue] = useState<string | null>(null);

  // Photo state
  const [photoUri, setPhotoUri] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  // Refreshing state
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  }, [refetch]);

  /**
   * Handle start speurtocht
   */
  const handleStart = async () => {
    if (!userTeam) {
      Alert.alert("Geen team", "Je moet in een team zitten om te starten");
      return;
    }

    Alert.alert(
      "Speurtocht Starten",
      `Wil je "${speurtocht?.name}" starten met team ${userTeam.name}?${
        speurtocht?.has_time_limit
          ? `\n\nJe hebt ${speurtocht.max_duration_minutes} minuten om te voltooien.`
          : ""
      }`,
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Starten",
          onPress: async () => {
            await startSpeurtocht();
          },
        },
      ]
    );
  };

  /**
   * Handle location check
   */
  const handleLocationCheck = async () => {
    if (!currentStep) return;

    // Check if we have valid coordinates
    if (!locationConfig || (locationConfig.lat === 0 && locationConfig.lng === 0)) {
      if (isGeocoding) {
        Alert.alert("Even geduld", "Adres wordt nog opgezocht...");
        return;
      }
      if (geocodeError) {
        Alert.alert("Locatie onbekend", "Het adres kon niet worden gevonden. Probeer de navigatie knop te gebruiken.");
        return;
      }
      Alert.alert("Locatie onbekend", "Er zijn geen coördinaten beschikbaar voor deze stap.");
      return;
    }

    // Request permission first if needed
    if (hasPermission === false) {
      Alert.alert(
        "Locatie Toestemming",
        "We hebben toegang tot je locatie nodig om te controleren of je op de juiste plek bent.",
        [
          { text: "Annuleren", style: "cancel" },
          {
            text: "Toestemming geven",
            onPress: async () => {
              const granted = await requestPermission();
              if (granted) {
                await getCurrentLocation();
              }
            },
          },
        ]
      );
      return;
    }

    const location = await getCurrentLocation();
    
    if (!location) {
      Alert.alert(
        "Locatie niet gevonden",
        "Kon je huidige locatie niet bepalen. Controleer of locatieservices zijn ingeschakeld.",
        [{ text: "OK" }]
      );
    }
  };

  /**
   * Handle QR code scan
   */
  const handleQRScan = (value: string) => {
    setScannedQRValue(value);
    setShowQRScanner(false);

    // Validate the QR code
    if (!currentStep) return;
    const config = currentStep.config as Record<string, unknown>;
    const expectedValue = String(config.expected_value || "");

    if (value === expectedValue) {
      Alert.alert("QR-code gescand! ✅", "Dat is de juiste code!");
    } else {
      Alert.alert(
        "Verkeerde QR-code",
        "Dit is niet de juiste QR-code. Probeer een andere te scannen."
      );
      setScannedQRValue(null);
    }
  };

  /**
   * Handle photo capture
   */
  const handleTakePhoto = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== "granted") {
      Alert.alert(
        "Camera Toegang",
        "We hebben toegang tot je camera nodig om foto's te maken."
      );
      return;
    }

    const result = await ImagePicker.launchCameraAsync({
      mediaTypes: "images",
      allowsEditing: false,
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setPhotoUri(result.assets[0].uri);
    }
  };

  /**
   * Upload photo to storage
   */
  const uploadPhoto = async (uri: string): Promise<string | null> => {
    if (!session || !currentStep) return null;

    setIsUploadingPhoto(true);
    try {
      // Create blob from URI
      const response = await fetch(uri);
      const blob = await response.blob();
      const fileName = `speurtocht/${speurtocht?.id}/${session.id}/${currentStep.id}_${Date.now()}.jpg`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from("photos")
        .upload(fileName, blob, {
          contentType: "image/jpeg",
          upsert: true,
        });

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("photos")
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (err) {
      console.error("Photo upload error:", err);
      Alert.alert("Upload Fout", "Kon de foto niet uploaden. Probeer opnieuw.");
      return null;
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  /**
   * Handle step completion
   */
  const handleCompleteStep = async () => {
    if (!currentStep) return;

    const config = currentStep.config as Record<string, unknown>;
    let photoUrl: string | undefined;

    // Validate based on step type
    switch (currentStep.step_type) {
      case "location":
        if (!isWithinRadius) {
          Alert.alert(
            "Niet dichtbij genoeg",
            "Je bent nog niet dichtbij genoeg bij de locatie."
          );
          return;
        }
        break;
      case "word":
      case "puzzle":
        if (!answer.trim()) {
          Alert.alert("Antwoord vereist", "Voer een antwoord in");
          return;
        }
        const correctAnswer = String(config.correct_answer || config.answer || "");
        const caseSensitive = Boolean(config.case_sensitive);
        const isCorrect = caseSensitive
          ? answer.trim() === correctAnswer
          : answer.trim().toLowerCase() === correctAnswer.toLowerCase();

        if (!isCorrect) {
          Alert.alert("Helaas", "Dat is niet het juiste antwoord. Probeer opnieuw!");
          return;
        }
        break;
      case "qr_code":
        if (!scannedQRValue) {
          Alert.alert("Scan vereist", "Scan eerst de QR-code");
          return;
        }
        const expectedValue = String(config.expected_value || "");
        if (scannedQRValue !== expectedValue) {
          Alert.alert("Verkeerde QR-code", "Scan de juiste QR-code");
          return;
        }
        break;
      case "photo":
        if (!photoUri) {
          Alert.alert("Foto vereist", "Maak eerst een foto");
          return;
        }
        // Upload photo
        const uploadedUrl = await uploadPhoto(photoUri);
        if (!uploadedUrl) return;
        photoUrl = uploadedUrl;
        break;
    }

    // Complete the step
    const result = await completeStep({
      answer: answer.trim() || scannedQRValue || undefined,
      photo_url: photoUrl,
      distance_from_target: distanceToTarget || 0,
      lat: currentLocation?.coords.latitude || 0,
      lng: currentLocation?.coords.longitude || 0,
      accuracy: currentLocation?.coords.accuracy || 0,
    });

    if (result) {
      setAnswer("");
      setScannedQRValue(null);
      setPhotoUri(null);
      setShowCelebration(true);

      // Play animation if available
      if (lottieRef.current) {
        lottieRef.current.play();
      }

      // Hide celebration after animation
      setTimeout(() => {
        setShowCelebration(false);
      }, 3000);
    }
  };

  /**
   * Check if step is completed
   */
  const isStepCompleted = (stepId: string) => {
    return completions.some((c: SpeurttochtStepCompletion) => c.step_id === stepId);
  };

  /**
   * Get step status icon
   */
  const getStepStatusIcon = (step: SpeurttochtStep, _index: number) => {
    if (isStepCompleted(step.id)) {
      return { icon: "checkmark-circle", color: "#10B981" };
    }
    if (currentStep?.id === step.id) {
      return { icon: "play-circle", color: STEP_TYPE_COLORS[step.step_type] };
    }
    return { icon: "ellipse-outline", color: colors.textTertiary };
  };

  // Loading state
  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["top", "left", "right"]}
      >
        <ActivityIndicator size="large" color="#10B981" />
      </SafeAreaView>
    );
  }

  // Error state
  if (!speurtocht) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["top", "left", "right"]}
      >
        <Text style={{ color: colors.text }}>Speurtocht niet gevonden</Text>
        <TouchableOpacity
          onPress={() => router.back()}
          className="mt-4 px-4 py-2 rounded-lg"
          style={{ backgroundColor: colors.surface }}
        >
          <Text style={{ color: colors.primary }}>Terug</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  const isCompleted = session?.status === "completed";
  const hasSession = !!session;

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        className="flex-1"
      >
        {/* Header */}
        <View className="px-4 py-4 flex-row items-center justify-between">
          <View className="flex-row items-center flex-1">
            <TouchableOpacity
              onPress={() => router.back()}
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: colors.surface }}
              accessibilityRole="button"
              accessibilityLabel="Terug"
            >
              <Ionicons name="arrow-back" size={22} color={colors.text} />
            </TouchableOpacity>
            <View className="flex-1">
              <Text
                className="text-xl font-bold"
                style={{ color: colors.text }}
                numberOfLines={1}
              >
                {speurtocht.name}
              </Text>
              {session && (
                <Text className="text-sm" style={{ color: isCompleted ? "#10B981" : speurtocht.color }}>
                  {isCompleted 
                    ? "✅ Voltooid!" 
                    : `Stap ${Math.min((session.current_step_index || 0) + 1, session.total_steps)} van ${session.total_steps}`
                  }
                </Text>
              )}
            </View>
          </View>

          {/* Timer - only show when session is active (not completed/expired) */}
          {session?.expires_at && session.status === "active" && (
            <Timer expiresAt={session.expires_at} colors={colors} />
          )}
        </View>

        <ScrollView
          className="flex-1"
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={handleRefresh}
              tintColor={colors.primary}
            />
          }
        >
          {/* Not started - Show intro */}
          {!hasSession && (
            <View className="px-4">
              {/* Speurtocht Card */}
              <View
                className="rounded-xl p-6 mb-4"
                style={{
                  backgroundColor: `${speurtocht.color}15`,
                  borderWidth: 2,
                  borderColor: speurtocht.color,
                }}
              >
                <View className="items-center mb-4">
                  <View
                    className="w-20 h-20 rounded-full items-center justify-center mb-4"
                    style={{ backgroundColor: `${speurtocht.color}25` }}
                  >
                    <Ionicons
                      name={speurtocht.icon as any}
                      size={40}
                      color={speurtocht.color}
                    />
                  </View>
                  <Text
                    className="text-xl font-bold text-center"
                    style={{ color: colors.text }}
                  >
                    {speurtocht.name}
                  </Text>
                  {speurtocht.description && (
                    <Text
                      className="text-center mt-2"
                      style={{ color: colors.textSecondary }}
                    >
                      {speurtocht.description}
                    </Text>
                  )}
                </View>

                {/* Stats */}
                <View className="flex-row justify-around mt-4">
                  <View className="items-center">
                    <Text className="text-2xl font-bold" style={{ color: colors.text }}>
                      {speurtocht.steps_count || steps.length}
                    </Text>
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      Stappen
                    </Text>
                  </View>
                  {speurtocht.has_time_limit && speurtocht.max_duration_minutes && (
                    <View className="items-center">
                      <Text className="text-2xl font-bold" style={{ color: colors.text }}>
                        {speurtocht.max_duration_minutes}
                      </Text>
                      <Text className="text-sm" style={{ color: colors.textSecondary }}>
                        Minuten
                      </Text>
                    </View>
                  )}
                  <View className="items-center">
                    <Text
                      className="text-2xl font-bold"
                      style={{ color: "#F59E0B" }}
                    >
                      {(speurtocht.steps_count || steps.length) * speurtocht.points_per_step +
                        speurtocht.completion_bonus}
                    </Text>
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      Max punten
                    </Text>
                  </View>
                </View>
              </View>

              {/* Start Button */}
              <TouchableOpacity
                onPress={handleStart}
                disabled={isSubmitting}
                className="py-4 rounded-xl items-center"
                style={{
                  backgroundColor: speurtocht.color,
                  opacity: isSubmitting ? 0.7 : 1,
                }}
              >
                {isSubmitting ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <View className="flex-row items-center">
                    <Ionicons name="play" size={24} color="#FFFFFF" />
                    <Text className="ml-2 text-lg font-bold text-white">
                      Start Speurtocht
                    </Text>
                  </View>
                )}
              </TouchableOpacity>
            </View>
          )}

          {/* Completed - Show results */}
          {isCompleted && session && (
            <View className="px-4">
              <View
                className="rounded-xl p-6 items-center"
                style={{ backgroundColor: "#10B98120" }}
              >
                <View
                  className="w-20 h-20 rounded-full items-center justify-center mb-4"
                  style={{ backgroundColor: "#10B98130" }}
                >
                  <Ionicons name="trophy" size={40} color="#10B981" />
                </View>
                <Text
                  className="text-2xl font-bold text-center"
                  style={{ color: "#10B981" }}
                >
                  Voltooid! 🎉
                </Text>
                <Text
                  className="text-center mt-2"
                  style={{ color: colors.textSecondary }}
                >
                  Je team heeft de speurtocht voltooid!
                </Text>

                {/* Points */}
                <View
                  className="mt-4 px-6 py-3 rounded-xl"
                  style={{ backgroundColor: "#F59E0B20" }}
                >
                  <Text className="text-3xl font-bold text-center" style={{ color: "#F59E0B" }}>
                    {session.total_points} punten
                  </Text>
                </View>
              </View>
            </View>
          )}

          {/* In Progress - Show current step */}
          {hasSession && !isCompleted && currentStep && (
            <View className="px-4">
              {/* Progress indicator */}
              <View className="flex-row mb-4">
                {steps.map((step: SpeurttochtStep, index: number) => {
                  const status = getStepStatusIcon(step, index);
                  return (
                    <View
                      key={step.id}
                      className="flex-1 items-center"
                      style={{ marginHorizontal: 2 }}
                    >
                      <View
                        className="h-1 rounded-full w-full"
                        style={{
                          backgroundColor: isStepCompleted(step.id)
                            ? "#10B981"
                            : currentStep.id === step.id
                            ? speurtocht.color
                            : colors.surfaceSecondary,
                        }}
                      />
                    </View>
                  );
                })}
              </View>

              {/* Current Step Card */}
              <View
                className="rounded-xl overflow-hidden"
                style={{
                  backgroundColor: colors.surface,
                  borderWidth: 2,
                  borderColor: STEP_TYPE_COLORS[currentStep.step_type],
                }}
              >
                {/* Step Header */}
                <View
                  className="p-4"
                  style={{ backgroundColor: `${STEP_TYPE_COLORS[currentStep.step_type]}15` }}
                >
                  <View className="flex-row items-center">
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-3"
                      style={{
                        backgroundColor: `${STEP_TYPE_COLORS[currentStep.step_type]}25`,
                      }}
                    >
                      <Ionicons
                        name={STEP_TYPE_CONFIG[currentStep.step_type].icon as any}
                        size={20}
                        color={STEP_TYPE_COLORS[currentStep.step_type]}
                      />
                    </View>
                    <View className="flex-1">
                      <Text
                        className="text-xs uppercase font-medium"
                        style={{ color: STEP_TYPE_COLORS[currentStep.step_type] }}
                      >
                        {STEP_TYPE_CONFIG[currentStep.step_type].label}
                      </Text>
                      <Text className="text-lg font-bold" style={{ color: colors.text }}>
                        {currentStep.title}
                      </Text>
                    </View>
                  </View>
                </View>

                {/* Step Content */}
                <View className="p-4">
                  {currentStep.description && (
                    <Text
                      className="text-base mb-4"
                      style={{ color: colors.textSecondary }}
                    >
                      {currentStep.description}
                    </Text>
                  )}

                  {/* Puzzle question */}
                  {currentStep.step_type === "puzzle" && (
                    <View
                      className="p-4 rounded-xl mb-4"
                      style={{ backgroundColor: colors.surfaceSecondary }}
                    >
                      <Text className="text-base" style={{ color: colors.text }}>
                        {(currentStep.config as Record<string, unknown>).question as string}
                      </Text>
                    </View>
                  )}

                  {/* Location step */}
                  {currentStep.step_type === "location" && (
                    <View className="mb-4">
                      {/* Show address if available */}
                      {locationConfig?.address && (
                        <View
                          className="p-3 rounded-xl mb-3"
                          style={{ backgroundColor: colors.surfaceSecondary }}
                        >
                          <View className="flex-row items-center">
                            <Ionicons name="location" size={18} color={STEP_TYPE_COLORS.location} />
                            <Text
                              className="ml-2 flex-1 font-medium"
                              style={{ color: colors.text }}
                            >
                              {locationConfig.address}
                            </Text>
                          </View>
                        </View>
                      )}

                      {/* Geocoding status */}
                      {isGeocoding && (
                        <View className="p-4 rounded-xl items-center mb-3" style={{ backgroundColor: colors.surfaceSecondary }}>
                          <ActivityIndicator color={STEP_TYPE_COLORS.location} />
                          <Text className="text-sm mt-2" style={{ color: colors.textSecondary }}>
                            Adres opzoeken...
                          </Text>
                        </View>
                      )}

                      {geocodeError && (
                        <View className="p-3 rounded-xl mb-3" style={{ backgroundColor: `${colors.error}20` }}>
                          <Text style={{ color: colors.error }}>{geocodeError}</Text>
                        </View>
                      )}

                      {locationError && (
                        <View className="p-3 rounded-xl mb-3" style={{ backgroundColor: `${colors.error}20` }}>
                          <View className="flex-row items-center">
                            <Ionicons name="warning" size={16} color={colors.error} />
                            <Text className="ml-2" style={{ color: colors.error }}>{locationError}</Text>
                          </View>
                        </View>
                      )}

                      {/* Distance display */}
                      {distanceToTarget !== null && (
                        <View
                          className="p-4 rounded-xl items-center"
                          style={{
                            backgroundColor: isWithinRadius ? "#10B98120" : colors.surfaceSecondary,
                          }}
                        >
                          <Text
                            className="text-3xl font-bold"
                            style={{ color: isWithinRadius ? "#10B981" : colors.text }}
                          >
                            {formatDistance(distanceToTarget)} {isWithinRadius ? "🎯" : "📍"}
                          </Text>
                          <Text
                            className="text-sm mt-1"
                            style={{ color: colors.textSecondary }}
                          >
                            {isWithinRadius
                              ? "Je bent er! ✅"
                              : "Afstand tot de locatie"}
                          </Text>
                        </View>
                      )}

                      {/* Action buttons */}
                      <View className="flex-row mt-3" style={{ gap: 8 }}>
                        {/* Location check button */}
                        <TouchableOpacity
                          onPress={handleLocationCheck}
                          disabled={isLocationLoading || isGeocoding}
                          className="flex-1 py-3 rounded-xl items-center flex-row justify-center"
                          style={{ backgroundColor: colors.surfaceSecondary }}
                        >
                          {isLocationLoading ? (
                            <ActivityIndicator color={colors.primary} />
                          ) : (
                            <>
                              <Ionicons
                                name="locate"
                                size={20}
                                color={colors.primary}
                              />
                              <Text
                                className="ml-2 font-medium"
                                style={{ color: colors.primary }}
                              >
                                Locatie checken
                              </Text>
                            </>
                          )}
                        </TouchableOpacity>

                        {/* Navigate button */}
                        <TouchableOpacity
                          onPress={handleOpenMaps}
                          disabled={isGeocoding}
                          className="py-3 px-4 rounded-xl items-center flex-row justify-center"
                          style={{ backgroundColor: STEP_TYPE_COLORS.location }}
                        >
                          <Ionicons name="navigate" size={20} color="#FFFFFF" />
                          <Text className="ml-2 font-medium text-white">
                            Navigeer
                          </Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  )}

                  {/* Text input for word/puzzle */}
                  {(currentStep.step_type === "word" || currentStep.step_type === "puzzle") && (
                    <View className="mb-4">
                      <TextInput
                        value={answer}
                        onChangeText={setAnswer}
                        placeholder="Jouw antwoord..."
                        placeholderTextColor={colors.textTertiary}
                        className="p-4 rounded-xl text-base"
                        style={{
                          backgroundColor: colors.surfaceSecondary,
                          color: colors.text,
                        }}
                        autoCapitalize="none"
                        autoCorrect={false}
                      />
                    </View>
                  )}

                  {/* QR Code Scanner */}
                  {currentStep.step_type === "qr_code" && (
                    <View className="mb-4">
                      {scannedQRValue ? (
                        <View
                          className="p-4 rounded-xl items-center"
                          style={{ backgroundColor: "#10B98120" }}
                        >
                          <Ionicons name="qr-code" size={40} color="#10B981" />
                          <Text
                            className="text-lg font-bold mt-2"
                            style={{ color: "#10B981" }}
                          >
                            QR-code gescand! ✅
                          </Text>
                          <TouchableOpacity
                            onPress={() => setScannedQRValue(null)}
                            className="mt-3"
                          >
                            <Text style={{ color: colors.primary }}>
                              Opnieuw scannen
                            </Text>
                          </TouchableOpacity>
                        </View>
                      ) : (
                        <TouchableOpacity
                          onPress={() => setShowQRScanner(true)}
                          className="p-6 rounded-xl items-center"
                          style={{ backgroundColor: colors.surfaceSecondary }}
                        >
                          <Ionicons
                            name="qr-code"
                            size={48}
                            color={STEP_TYPE_COLORS.qr_code}
                          />
                          <Text
                            className="font-bold mt-3"
                            style={{ color: colors.text }}
                          >
                            Scan QR-code
                          </Text>
                          <Text
                            className="text-sm mt-1"
                            style={{ color: colors.textSecondary }}
                          >
                            Tik om de scanner te openen
                          </Text>
                        </TouchableOpacity>
                      )}
                      {typeof (currentStep.config as Record<string, unknown>).location_hint === 'string' && (
                        <Text
                          className="text-sm mt-2 text-center"
                          style={{ color: colors.textSecondary }}
                        >
                          💡 {String((currentStep.config as Record<string, unknown>).location_hint)}
                        </Text>
                      )}
                    </View>
                  )}

                  {/* Photo Capture */}
                  {currentStep.step_type === "photo" && (
                    <View className="mb-4">
                      {photoUri ? (
                        <View className="rounded-xl overflow-hidden">
                          <Image
                            source={{ uri: photoUri }}
                            className="w-full h-64 rounded-xl"
                            resizeMode="cover"
                          />
                          <TouchableOpacity
                            onPress={handleTakePhoto}
                            className="absolute bottom-3 right-3 px-4 py-2 rounded-lg"
                            style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
                          >
                            <Text className="text-white font-medium">
                              Opnieuw
                            </Text>
                          </TouchableOpacity>
                        </View>
                      ) : (
                        <TouchableOpacity
                          onPress={handleTakePhoto}
                          className="p-8 rounded-xl items-center"
                          style={{
                            backgroundColor: colors.surfaceSecondary,
                            borderWidth: 2,
                            borderStyle: "dashed",
                            borderColor: colors.border,
                          }}
                        >
                          <Ionicons
                            name="camera"
                            size={48}
                            color={STEP_TYPE_COLORS.photo}
                          />
                          <Text
                            className="font-bold mt-3"
                            style={{ color: colors.text }}
                          >
                            Maak een foto
                          </Text>
                          <Text
                            className="text-sm mt-1 text-center"
                            style={{ color: colors.textSecondary }}
                          >
                            Tik om je camera te openen
                          </Text>
                        </TouchableOpacity>
                      )}
                      {Array.isArray((currentStep.config as Record<string, unknown>).requirements) && (
                        <View className="mt-3">
                          <Text
                            className="text-sm font-medium mb-2"
                            style={{ color: colors.textSecondary }}
                          >
                            Vereisten:
                          </Text>
                          {((currentStep.config as Record<string, unknown>).requirements as string[]).map((req, idx) => (
                            <View key={idx} className="flex-row items-center mb-1">
                              <Ionicons
                                name="checkmark-circle-outline"
                                size={16}
                                color={colors.textSecondary}
                              />
                              <Text
                                className="ml-2 text-sm"
                                style={{ color: colors.textSecondary }}
                              >
                                {req}
                              </Text>
                            </View>
                          ))}
                        </View>
                      )}
                    </View>
                  )}

                  {/* Hint */}
                  {currentStep.hint && (
                    <TouchableOpacity
                      onPress={() => setShowHint(!showHint)}
                      className="mb-4 flex-row items-center"
                    >
                      <Ionicons
                        name={showHint ? "bulb" : "bulb-outline"}
                        size={18}
                        color="#F59E0B"
                      />
                      <Text className="ml-2" style={{ color: "#F59E0B" }}>
                        {showHint ? "Hint verbergen" : "Hint tonen"}
                      </Text>
                    </TouchableOpacity>
                  )}
                  {showHint && currentStep.hint && (
                    <View
                      className="p-3 rounded-xl mb-4"
                      style={{ backgroundColor: "#F59E0B20" }}
                    >
                      <Text style={{ color: "#F59E0B" }}>{currentStep.hint}</Text>
                    </View>
                  )}

                  {/* Complete Button */}
                  {(() => {
                    const canComplete =
                      (currentStep.step_type === "location" && isWithinRadius) ||
                      (currentStep.step_type === "qr_code" && scannedQRValue) ||
                      (currentStep.step_type === "photo" && photoUri) ||
                      currentStep.step_type === "word" ||
                      currentStep.step_type === "puzzle";

                    return (
                      <TouchableOpacity
                        onPress={handleCompleteStep}
                        disabled={isSubmitting || isUploadingPhoto || !canComplete}
                        className="py-4 rounded-xl items-center"
                        style={{
                          backgroundColor: canComplete
                            ? STEP_TYPE_COLORS[currentStep.step_type]
                            : colors.surfaceSecondary,
                          opacity: isSubmitting || isUploadingPhoto ? 0.7 : 1,
                        }}
                      >
                        {isSubmitting || isUploadingPhoto ? (
                          <View className="flex-row items-center">
                            <ActivityIndicator color="#FFFFFF" />
                            {isUploadingPhoto && (
                              <Text className="ml-2 text-white font-medium">
                                Foto uploaden...
                              </Text>
                            )}
                          </View>
                        ) : (
                          <View className="flex-row items-center">
                            <Ionicons
                              name="checkmark-circle"
                              size={24}
                              color={canComplete ? "#FFFFFF" : colors.textTertiary}
                            />
                            <Text
                              className="ml-2 text-lg font-bold"
                              style={{
                                color: canComplete ? "#FFFFFF" : colors.textTertiary,
                              }}
                            >
                              Stap Voltooien
                            </Text>
                          </View>
                        )}
                      </TouchableOpacity>
                    );
                  })()}
                </View>
              </View>
            </View>
          )}

          <View className="h-32" />
        </ScrollView>

        {/* Celebration Overlay */}
        {showCelebration && (
          <View
            className="absolute inset-0 items-center justify-center"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <View
              className="p-8 rounded-2xl items-center"
              style={{ backgroundColor: colors.background }}
            >
              <LottieView
                ref={lottieRef}
                source={
                  // Use step-specific lottie, or speurtocht default, or fallback to confetti
                  getLottieSource(currentStep?.lottie_animation || "") ||
                  getLottieSource(speurtocht.step_completion_lottie || "") ||
                  getLottieSource("confetti")
                }
                style={{ width: 150, height: 150 }}
                autoPlay
                loop={false}
              />
              <Text
                className="text-2xl font-bold mt-4"
                style={{ color: "#10B981" }}
              >
                Goed gedaan! 🎉
              </Text>
              <Text
                className="text-center mt-2"
                style={{ color: colors.textSecondary }}
              >
                +{speurtocht.points_per_step} punten
              </Text>
            </View>
          </View>
        )}

        {/* QR Code Scanner Modal */}
        <QRCodeScanner
          visible={showQRScanner}
          onScan={handleQRScan}
          onClose={() => setShowQRScanner(false)}
          hint={(currentStep?.config as Record<string, unknown>)?.location_hint as string}
        />
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

